import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  useTheme,
  alpha,
  IconButton,
  Stack,
  Divider,
  Button,
  TextField,
} from "@mui/material";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import SendIcon from "@mui/icons-material/Send";

export const Footer = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: alpha(theme.palette.primary.dark, 0.9),
        color: alpha(theme.palette.primary.contrastText, 0.7),
        pt: { xs: 6, md: 8 },
      }}
    >
      <Container maxWidth="lg">
        {/* Newsletter Section */}
        <Box
          sx={{
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
            py: { xs: 4, md: 5 },
            px: { xs: 2, md: 4 },
            borderRadius: theme.shape.borderRadius * 1.5,
            mb: { xs: 6, md: 8 },
            mt: -12,
            position: "relative",
            boxShadow: `0 10px 25px -5px ${alpha(
              theme.palette.secondary.dark,
              0.4
            )}`,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, mb: { xs: 1, md: 0 } }}
              >
                Fique por dentro!
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Receba dicas e ofertas exclusivas direto no seu e-mail.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  placeholder="Seu melhor e-mail"
                  variant="outlined"
                  size="small"
                  sx={{
                    backgroundColor: alpha("#fff", 0.2),
                    borderRadius: 1,
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": {
                        borderColor: "transparent",
                      },
                      "&:hover fieldset": {
                        borderColor: alpha("#fff", 0.5),
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "white",
                      },
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: alpha("#fff", 0.8),
                      opacity: 1,
                    },
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  endIcon={<SendIcon />}
                  sx={{ py: 1.1, px: 3, whiteSpace: "nowrap" }}
                >
                  Cadastrar
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* Footer Links Section */}
        <Grid container spacing={4} sx={{ pb: 6 }}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <WbSunnyIcon
                sx={{ color: theme.palette.primary.main, fontSize: 32 }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.light,
                }}
              >
                Sol Plano
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Encontrando o brilho certo para a sua saúde. Comparamos as
              melhores opções para você.
            </Typography>
            <Stack direction="row" spacing={1.5}>
              {[FacebookRoundedIcon, InstagramIcon, TwitterIcon].map(
                (Icon, index) => (
                  <IconButton
                    key={index}
                    aria-label={Icon.name?.replace("Icon", "").toLowerCase()}
                    sx={{
                      color: theme.palette.primary.contrastText,
                      backgroundColor: alpha(theme.palette.primary.light, 0.3),
                      "&:hover": {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                      },
                      borderRadius: 1,
                    }}
                  >
                    <Icon fontSize="small" />
                  </IconButton>
                )
              )}
            </Stack>
          </Grid>

          <Grid item xs={6} md={2}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "#ffffff", mb: 2 }}
            >
              Navegação
            </Typography>
            <Stack spacing={1}>
              {["Diferenciais", "Planos", "Como Funciona", "Contato"].map(
                (item) => (
                  <Link
                    key={item}
                    href={`#${item.toLowerCase().replace(" ", "-")}`}
                    underline="hover"
                    sx={{
                      color: alpha("#ffffff", 0.8),
                      "&:hover": { color: theme.palette.primary.light },
                    }}
                  >
                    {item}
                  </Link>
                )
              )}
            </Stack>
          </Grid>

          <Grid item xs={6} md={3}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "#ffffff", mb: 2 }}
            >
              Suporte
            </Typography>
            <Stack spacing={1}>
              {[
                "Central de Ajuda",
                "FAQ",
                "Política de Privacidade",
                "Termos de Uso",
              ].map((item) => (
                <Link
                  key={item}
                  href="#"
                  underline="hover"
                  sx={{
                    color: alpha("#ffffff", 0.8),
                    "&:hover": { color: theme.palette.primary.light },
                  }}
                >
                  {item}
                </Link>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "#ffffff", mb: 2 }}
            >
              Contato Rápido
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              WhatsApp: (11) 98877-6655
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              E-mail: contato@solplano.com.br
            </Typography>
            <Typography variant="body2">
              Atendimento: Seg a Sex, 9h às 18h
            </Typography>
          </Grid>
        </Grid>

        {/* Copyright Section */}
        <Divider sx={{ borderColor: alpha("#ffffff", 0.2) }} />
        <Box
          sx={{
            py: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Typography variant="body2" sx={{ color: alpha("#ffffff", 0.7) }}>
            © {currentYear} Sol Plano. Todos os direitos reservados.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
